import { Endpoints } from "./endpoints"
import { Platform } from "./platform"
import { ProfileTestObject } from "./profileTestObject"
import { SessionConfig } from "./sessionConfig"

export interface PlatformTestObject {
  cdnResponseHeader: string | undefined
  dopplerPlatformID: string
  clientEndpoints: Endpoints
  hdxEnabled: boolean
  hdxEnabledDefault: boolean
  includePulsar: boolean
  pulsarAppID: string | undefined
  pulsarJobID: string | undefined
  /**
   * The desired number of measurements per hour. Note that this might be a
   * decimal number.
   */
  targetFrequency: number
  testObjectHostname: string
  testObjectPath: string
  testObjectSize: number
  /**
   * Milliseconds before aborting fetch and reporting an availability issue.
   */
  testObjectTimeout: number
}

export const createPlatformTestObject: (
  sessionConfig: SessionConfig,
  profileTestObject: ProfileTestObject,
  platform: Platform,
) => PlatformTestObject = (s, pr, pl) => new PlatformTestObjectImpl(s, pr, pl)

class PlatformTestObjectImpl implements PlatformTestObject {
  private _cdnResponseHeader
  private _dopplerPlatformID
  private _clientEndpoints
  private _hdxEnabled
  private _hdxEnabledDefault
  private _includePulsar
  private _pulsarAppID
  private _pulsarJobID
  private _targetFrequency
  private _testObjectHostname
  private _testObjectPath
  private _testObjectSize
  private _testObjectTimeout
  constructor(
    sessionConfig: SessionConfig,
    profileTestObject: ProfileTestObject,
    /**
     * The parent {@link Platform} of the test object
     */
    platform: Platform,
  ) {
    this._cdnResponseHeader = platform.cdnResponseHeader
    this._dopplerPlatformID = platform.dopplerPlatformID
    this._clientEndpoints = sessionConfig.beaconEndpointInfo
    this._pulsarAppID = platform.pulsarAppID
    this._pulsarJobID = platform.pulsarJobID
    let tempTargetFrequency = profileTestObject.targetFrequency
    this._hdxEnabled = platform.platformProfile.profileFeatures.hdxEnabled
    this._hdxEnabledDefault =
      sessionConfig.dopplerClientDefaults.platformDefaults.hdxEnabled
    // See if there's a object-level override for this object's size
    const overrideEntry =
      platform.testObjectOverrides &&
      platform.testObjectOverrides.find(
        (entry) => entry.testObjectSize === profileTestObject.testObjectSize,
      )
    if (overrideEntry && typeof overrideEntry.targetFrequency === "number") {
      tempTargetFrequency = overrideEntry.targetFrequency
    }
    this._targetFrequency = Math.min(
      tempTargetFrequency,
      // sensible upper limit (120 means approximately every 30 seconds)
      120,
    )
    /* eslint-disable @typescript-eslint/dot-notation */
    // TODO: `pulsarMetrics` is an array for historical reasons, but it
    // would be good to replace this in the session config with a
    // simple flag
    const pulsarMetrics =
      (profileTestObject.pulsarMetrics &&
        profileTestObject.pulsarMetrics["pulsar"]) ||
      []
    /* eslint-enable @typescript-eslint/dot-notation */
    this._includePulsar = 0 < pulsarMetrics.length
    this._testObjectHostname = platform.platformHostname
    this._testObjectPath = profileTestObject.testObjectPath
    this._testObjectSize = profileTestObject.testObjectSize
    this._testObjectTimeout = Math.floor(
      1000 *
        Math.min(
          profileTestObject.testObjectTimeout,
          10, // sensible upper limit
        ),
    )
  }

  get cdnResponseHeader() {
    return this._cdnResponseHeader
  }

  get clientEndpoints() {
    return this._clientEndpoints
  }

  get dopplerPlatformID() {
    return this._dopplerPlatformID
  }

  get hdxEnabled() {
    return this._hdxEnabled
  }

  get hdxEnabledDefault() {
    return this._hdxEnabledDefault
  }

  get includePulsar() {
    return this._includePulsar
  }

  get pulsarAppID() {
    return this._pulsarAppID
  }

  get pulsarJobID() {
    return this._pulsarJobID
  }

  get targetFrequency() {
    return this._targetFrequency
  }

  get testObjectHostname() {
    return this._testObjectHostname
  }

  get testObjectPath() {
    return this._testObjectPath
  }

  get testObjectSize() {
    return this._testObjectSize
  }

  get testObjectTimeout() {
    return this._testObjectTimeout
  }
}
